import { renderAgentLogsView } from './agent_logs.js'
/**
 * Galaxy Fleet View — dashboard with run cards, inline DAGs, queue, recent runs.
 *
 * Composes: phaseRiver (compact DAG), sidebar
 * Data: fetchFleetDashboard() — single API call
 * CSS: styles/views/fleet.css, styles/components/page-header.css
 */

import { route, navigate } from '../../router.js'
import { esc } from '../../shared/rendering.js'
import { capitalizeLabel, formatTime, formatStageName, formatDuration } from '../../types/domain.js'
import { action } from '../components/action-delegation.js'
import { withErrorBoundary } from '../../otel.js'
import { renderCompactDag } from '../components/phaseRiver.js'
import { renderFleetSkeleton } from '../components/skeleton.js'
import { renderCopyBtn } from '../components/copyButton.js'
import { fetchFleetDashboard } from '../../api/fleet.js'
import type {
  FleetDashboard,
  ActiveRunWithGraph,
  RecentRun,
  ReadyWorkItem,
} from '../../types/api.js'

// ─── Route Registration ─────────────────────────────────────────────────────

route('/', () => {
  void renderFleetView()
})

route('/logs', () => {
  void renderAgentLogsView()
})

// Concurrency contract: _fleetGen is incremented on each renderFleetView() call.
// Async continuations compare their captured gen against _fleetGen before writing
// to the DOM; stale responses from superseded navigations are silently dropped.
// _subtitleTimer is always cleared before re-assignment — at most one subtitle
// ticker runs at a time. All three variables are accessed only from the main JS
// thread (single-threaded browser event loop); no mutex is required.
let _fleetGen = 0
let _lastFetchAt: number | null = null // epoch ms of last successful fetch; null before first
let _subtitleTimer: ReturnType<typeof setInterval> | null = null

function formatFetchAge(): string {
  if (_lastFetchAt === null) {
    return 'just now'
  }
  const s = Math.round((Date.now() - _lastFetchAt) / 1000)
  if (s < 5) {
    return 'just now'
  }
  if (s < 60) {
    return `${s}s ago`
  }
  const m = Math.floor(s / 60)
  return `${m}m ago`
}

function writeSubtitle(running: number): void {
  const subtitle = document.getElementById('fleet-subtitle')
  if (!subtitle) {
    return
  }
  subtitle.textContent = `${running} agent${running !== 1 ? 's' : ''} active \u00b7 updated ${formatFetchAge()}`
}

// ─── View Renderer ──────────────────────────────────────────────────────────

async function renderFleetView(): Promise<void> {
  const gen = ++_fleetGen
  const app = document.getElementById('app')
  if (!app) {
    return
  }

  // Set title and render loading state
  document.title = 'Fleet Overview \u2014 Grizzly'
  app.innerHTML = `<header class="page-header">
    <div><h1 class="page-title">Fleet Overview</h1><p class="page-subtitle" id="fleet-subtitle">Loading...</p></div>
    <button class="cmd-k" ${action('openCommandPalette')}>Search <kbd>\u2318K</kbd></button>
  </header>
  <div id="fleet-content">${renderFleetSkeleton()}</div>`

  try {
    const dashboard = await withErrorBoundary('api.fleet.dashboard', () => fetchFleetDashboard())
    if (_fleetGen !== gen) {
      return
    }
    renderDashboard(dashboard)
    // preventScroll: keep the sticky topbar pinned at viewport-top.
    // Default focus() triggers scrollIntoView on the focused element,
    // which can force the browser to scroll the sticky topbar out of
    // frame momentarily (observed as "topbar flashed and disappeared").
    document.getElementById('app')?.focus({ preventScroll: true })
  } catch (err) {
    if (_fleetGen !== gen) {
      return
    }
    const contentEl = document.getElementById('fleet-content')
    if (contentEl) {
      contentEl.innerHTML = `<div class="empty">Failed to load fleet data. ${esc(String(err))}</div>`
    }
  }
}

function renderDashboard(data: FleetDashboard): void {
  const running = data.active_runs.filter((run) => run.status === 'running').length
  const content = document.getElementById('fleet-content')
  if (!content) {
    return
  }

  _lastFetchAt = Date.now()
  writeSubtitle(running)
  if (_subtitleTimer !== null) {
    clearInterval(_subtitleTimer)
  }
  // 5 s: accurate enough for "Xs ago" UX (±5 s error) without excess CPU cost
  _subtitleTimer = setInterval(() => {
    writeSubtitle(running)
  }, 5000)

  const activeSection = renderActiveRuns(data.active_runs)
  const queueSection = renderReadyQueue(data.ready_queue)
  const recentSection = renderRecentRuns(data.recent_runs)

  const failureAlert =
    data.summary.failed_today > 0
      ? `<div class="fleet-alert">${data.summary.failed_today} failure${data.summary.failed_today === 1 ? '' : 's'} today — inspect Recent Runs before starting more work.</div>`
      : ''

  content.innerHTML = `
    ${failureAlert}
    ${activeSection}
    <div style="height:1px;background:linear-gradient(90deg,transparent,var(--bdh),transparent);margin:24px 0 20px"></div>
    <div class="two-col">
      <div>${queueSection}</div>
      <div>${recentSection}</div>
    </div>`
}

// ─── Active Runs ────────────────────────────────────────────────────────────

function renderActiveRuns(runs: readonly ActiveRunWithGraph[]): string {
  const running = runs.filter((r) => r.status === 'running').length
  const failed = runs.filter((r) => r.status === 'failed').length
  const countText = [running ? `${running} running` : '', failed ? `${failed} failed` : '']
    .filter(Boolean)
    .join(' \u00b7 ')

  const cards = runs.map((run) => renderRunCard(run)).join('')

  return `<div class="section-header">
    <h2 class="section-title">Active Runs</h2>
    <span class="section-count">${esc(countText || '0')}</span>
  </div>
  ${cards || '<div class="empty">No active runs</div>'}`
}

function renderRunCard(run: ActiveRunWithGraph): string {
  const isDead = run.status === 'failed'
  const cardClass = isDead ? 'dead' : run.status === 'completed' ? 'completed' : ''
  const statusColor = isDead ? 'err' : run.status === 'completed' ? 'grn' : 'cyn'
  const statusText = run.current_stage
    ? `${run.iteration ? `Iter ${run.iteration}` : ''} · ${formatStageName(run.current_stage)}`
    : run.status
  const elapsed = formatDuration(run.elapsed_s) ?? ''
  const dagHtml = renderCompactDag(run.execution_graph)
  const title = run.task_title ?? run.work_item_id
  const identity =
    run.work_item_id === run.run_id
      ? `${esc(run.run_id)} ${renderCopyBtn(run.run_id)}`
      : `${esc(run.work_item_id)} ${renderCopyBtn(run.work_item_id)} · ${esc(run.run_id)} ${renderCopyBtn(run.run_id)}`

  return `<div class="run-card ${cardClass}" role="link" tabindex="0" aria-label="${esc(title)}" ${action('navigate', `/run/${run.run_id}`)}>
    <div class="run-card-head">
      <div class="rc-main">
        <span class="rc-title">${esc(title)}</span>
        <span class="rc-meta">${identity}</span>
      </div>
      <span class="rc-pill" style="background:var(--${statusColor}-d);color:var(--${statusColor})">${esc(statusText)}</span>
      <span class="rc-time">started ${esc(formatTime(run.started_at))}</span>
      <span class="rc-elapsed">${esc(elapsed)}</span>
    </div>
    ${dagHtml}
  </div>`
}

// ─── Ready Queue ────────────────────────────────────────────────────────────

function renderReadyQueue(items: readonly ReadyWorkItem[]): string {
  if (items.length === 0) {
    return `<div class="section-header"><h2 class="section-title">Ready Queue</h2><span class="section-count">0</span></div>
    <div class="empty">No items in queue</div>`
  }

  const rows = items
    .map((item) => {
      const typeClass = item.type === 'story' ? 'story' : 'task'
      const prioClass =
        item.priority === 'critical'
          ? 'crit'
          : item.priority === 'high'
            ? 'high'
            : item.priority === 'medium'
              ? 'med'
              : 'low'
      const priorityHtml =
        item.priority === 'medium'
          ? '<span class="q-prio-muted">Default</span>'
          : `<span class="q-dot ${prioClass}"></span>${esc(capitalizeLabel(item.priority))}`
      return `<tr role="link" tabindex="0" aria-label="${esc(item.title)}" ${action('navigate', `/work/${item.id}`)}>
      <td class="q-id" data-label="ID">${esc(item.id)}</td>
      <td class="q-title" data-label="Title">${esc(item.title)}</td>
      <td data-label="Type"><span class="q-type ${typeClass}">${esc(capitalizeLabel(item.type))}</span></td>
      <td class="q-prio" data-label="Priority">${priorityHtml}</td>
    </tr>`
    })
    .join('')

  return `<div class="section-header"><h2 class="section-title">Ready Queue</h2><span class="section-count">${items.length}</span></div>
  <table class="queue-table">
    <thead><tr><th style="width:80px">ID</th><th>Title</th><th style="width:55px">Type</th><th style="width:70px">Priority</th></tr></thead>
    <tbody>${rows}</tbody>
  </table>`
}

// ─── Recent Runs ────────────────────────────────────────────────────────────

function renderRecentRuns(runs: readonly RecentRun[]): string {
  if (runs.length === 0) {
    return `<div class="section-header"><h2 class="section-title">Recent Runs</h2><span class="section-count">0</span></div>
    <div class="empty">No recent runs</div>`
  }

  const rows = runs
    .map((run) => {
      const dotClass =
        run.status === 'completed' ? 'ok' : run.status === 'failed' ? 'fail' : 'running'
      const stageClass =
        run.status === 'completed'
          ? 'completed'
          : run.status === 'failed'
            ? 'failed'
            : 'in-progress'
      const stageLabel =
        run.status === 'completed' ? 'Completed' : run.status === 'failed' ? 'Failed' : 'Running'
      const elapsed = formatDuration(run.elapsed_s) ?? ''
      return `<div class="run-row" role="link" tabindex="0" aria-label="${esc(run.task_title ?? run.work_item_id)}" ${action('navigate', `/run/${run.run_id}`)}>
      <span class="run-dot ${dotClass}"></span>
      <div class="run-info">
        <div class="run-task">${esc(run.task_title ?? run.work_item_id)}</div>
        <div class="run-id">${esc(run.work_item_id)} \u00b7 ${esc(run.run_id)}</div>
      </div>
      <span class="run-stage ${stageClass}">${stageLabel}</span>
      <span class="run-time">${esc(elapsed)}</span>
    </div>`
    })
    .join('')

  return `<div class="section-header"><h2 class="section-title">Recent Runs</h2><span class="section-count">${runs.length}</span></div>
  <div class="run-list">${rows}</div>`
}

// ─── Expose navigate globally for onclick handlers ──────────────────────────

// navigate is imported from router.ts but needs to be on window for onclick=""
// This is handled by app.ts — we just need the import for TypeScript resolution
void navigate
